import { useEffect } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { RouterProvider } from 'react-router-dom';
import { router } from '@/router';
import { useSettingsStore } from '@/store/settings';
import { useAuthStore } from '@/store/auth';
import { antdConfig, watchSystemTheme } from '@/theme';
import { setAppApi } from '@/utils/error';
import { ErrorBoundary } from '@/components';

/**
 * 把 App.useApp() 的实例交给非 React 的工具模块。
 * 必须是 <AntdApp> 的**子组件**——useApp 读的是它提供的 context。
 * 不这么做的话 utils/error.ts 只能用静态 Modal/message，那套走不到 ConfigProvider，
 * 深色模式下会弹出白底对话框。
 */
function AntdApiBridge() {
  const { message, modal } = AntdApp.useApp();
  useEffect(() => {
    setAppApi({ message, modal });
    return () => setAppApi(null);
  }, [message, modal]);
  return null;
}

export default function App() {
  const themeMode = useSettingsStore((s) => s.themeMode);
  const resolvedMode = useSettingsStore((s) => s.resolvedMode);
  const syncSystemTheme = useSettingsStore((s) => s.syncSystemTheme);
  const authBootstrap = useAuthStore((s) => s.bootstrap);

  // 登录态探测放在这里而不是只放在守卫里：直接打开 /login 时也要知道
  // needs_bootstrap，否则首次部署的用户看到的是登录表单而不是「创建管理员」。
  // store 内部对重复调用做了短路，与守卫里的那次不会打两遍接口。
  useEffect(() => {
    void authBootstrap();
  }, [authBootstrap]);

  // 只在跟随系统时订阅。固定浅/深色的用户不需要为系统切换付出一次重渲染，
  // 更重要的是：不订阅就不会在用户已经明确选过之后被系统偏好覆盖。
  useEffect(() => {
    if (themeMode !== 'system') return;
    // 订阅期间系统可能已经变过（比如标签页在后台时到了日落），先对齐一次
    syncSystemTheme();
    return watchSystemTheme(syncSystemTheme);
  }, [themeMode, syncSystemTheme]);

  return (
    <ConfigProvider locale={zhCN} theme={antdConfig(resolvedMode)}>
      <AntdApp>
        <AntdApiBridge />
        <ErrorBoundary scope="应用">
          <RouterProvider router={router} />
        </ErrorBoundary>
      </AntdApp>
    </ConfigProvider>
  );
}
